// external libs
import { ofType } from 'redux-observable'
import { from, of } from 'rxjs'
import { switchMap, mergeMap, catchError } from 'rxjs/operators'
import GuardXLogger from 'guard-x-logger'

import SuriApi from '../../Services/SuriApi'
import logDataSerializer from '../Serializers/LogDataSerializer'

// redux
import QuoteActions, { QuoteTypes } from '../Redux/QuoteRedux'
import GeneralActions, { ERROR_TYPES } from '../Redux/GeneralRedux'
import ValidationActions from '../Redux/ValidationRedux'
import InputHelpsActions from '../Redux/InputHelpsRedux'
import FilterActions from '../Redux/FilterRedux'
import NavigationActions from '../Redux/NavigationRedux'
import UserDataActions from '../Redux/UserDataRedux'
import LinkBuilderActions from '../Redux/LinkBuilderRedux'

import { getStepUrl } from '../ContainerFunctions'

const QuoteEpics = [
  (action$, state$) =>
    action$.pipe(
      ofType(QuoteTypes.QUOTE_ASYNC_REQUEST),
      switchMap((action) =>
        from(
          SuriApi.quoteAsync(action.product, {
            ...action.data,
            properties: {
              ...action.data.properties,
              ...state$.value.userData.getIn(['properties'], {}),
              ...(typeof action.linkData === 'object' ? action.linkData : {}),
            },
          }),
        ).pipe(
          switchMap((response) => {
            const stepUrl = getStepUrl(action.subProductName, 'quotes')
            if (response.data.content.expectedQuotes <= 0) {
              GuardXLogger.captureMessage('QUOTE_ASYNC_REQUEST', {
                extra: {
                  action: 'QUOTE_ASYNC_REQUEST',
                  response: logDataSerializer(response.data),
                  data: logDataSerializer(action.data),
                  subProductName: action.subProductName,
                  severity: 3,
                  resource: {
                    type: 'logging_log',
                    labels: {
                      name: 'no quotes',
                    },
                  },
                },
              })
            }
            const actions = [
              QuoteActions.quoteAsyncRequestSuccess(
                response.data.content.expectedQuotes,
                response.data.content.request.id,
                action.quotesKey,
              ),
              UserDataActions.onSetRequestId(response.data.content.request.id),
              NavigationActions.onSetLastValidStep('quotes', stepUrl),
              GeneralActions.onChangeRedirectUrl(stepUrl),
            ]
            actions.push(
              GeneralActions.logToStackdriver('async-request', {
                expectedQuotes: response.data && response.data.content && response.data.content.expectedQuotes,
              }),
            )
            return from(actions)
          }),
          catchError((e) => {
            GuardXLogger.captureException(e, {
              extra: {
                action: 'QUOTE_ASYNC_REQUEST',
                response: logDataSerializer(e.response),
                data: logDataSerializer(action.data),
                subProductName: action.subProductName,
                severity: 3,
                resource: {
                  type: 'logging_log',
                  labels: {
                    name: 'no quotes',
                  },
                },
              },
            })
            return of(GeneralActions.onChangeRequestFailure(e))
          }),
        ),
      ),
    ),
  (action$) =>
    action$.pipe(
      ofType(QuoteTypes.GET_QUOTE_REQUEST),
      switchMap((action) =>
        from(SuriApi.getQuote(action.calculatorName, action.data)).pipe(
          switchMap((response) =>
            from([
              QuoteActions.refreshQuoteRequestSuccess(response.data.content.quote, action.quotesKey),
              UserDataActions.onSetRequestId(response.data.content.request.id),
              InputHelpsActions.quoteInputHelpsRefresh(action.oldId, response.data.content.quote.id, {}),
              FilterActions.onRefreshFilteredData(action.oldId, response.data.content.quote.id, action.quotesKey),
            ]),
          ),
          catchError((e) => {
            GuardXLogger.captureException(e, {
              extra: {
                action: 'GET_QUOTE_REQUEST',
                response: logDataSerializer(e.response),
                data: logDataSerializer(action.data),
                calculatorName: action.calculatorName,
                severity: 3,
                resource: {
                  type: 'logging_log',
                  labels: {
                    name: 'no quotes',
                  },
                },
              },
            })
            return from([
              NavigationActions.onSetLastValidStepIndex('product'),
              GeneralActions.onChangeRequestFailure(e),
            ])
          }),
        ),
      ),
    ),
  (action$) =>
    action$.pipe(
      ofType(QuoteTypes.GET_QUOTE_SUPPLEMENTARY_INSURANCE_PRICE_REQUEST),
      mergeMap((action) =>
        from(SuriApi.getQuote(action.quote.calculatorName, action.data)).pipe(
          mergeMap((response) =>
            from([
              QuoteActions.getQuoteSupplementaryInsurancePriceRequestSuccess(response.data.content.quote, action.quote),
              LinkBuilderActions.setLinkSaveRequestSig(true),
            ]),
          ),
          catchError((e) => {
            GuardXLogger.captureException(e, {
              extra: {
                action: 'GET_QUOTE_SUPPLEMENTARY_INSURANCE_PRICE_REQUEST',
                response: logDataSerializer(e.response),
                data: logDataSerializer(action.data),
                calculatorName: action.quote.calculatorName,
                severity: 3,
                resource: {
                  type: 'logging_log',
                  labels: {
                    name: 'no quotes',
                  },
                },
              },
            })
            return from([
              UserDataActions.supplementaryInsuranceReset(action.quote.uniqueQuoteId, action.backupData),
              QuoteActions.resetLastSupplementaryInsuranceRequestData(action.quote.uniqueQuoteId, action.backupData),
              ValidationActions.onChangeGeneralFailureError(
                new Error('cannot_get_quote_price'),
                'cannot_get_quote_price',
                'cannot_get_quote_price',
              ),
              GeneralActions.onChangeRequestFailure(e, ERROR_TYPES.WARNING),
            ])
          }),
        ),
      ),
    ),
  (action$) =>
    action$.pipe(
      ofType(QuoteTypes.QUOTE_CONTENT_REQUEST),
      switchMap((action) =>
        from(SuriApi.getQuoteContent(action.productName)).pipe(
          switchMap((response) => of(QuoteActions.quoteContentRequestSuccess(response.data.data))),
          catchError((e) => of(GeneralActions.onChangeRequestFailure(e))),
        ),
      ),
    ),
  (action$) =>
    action$.pipe(
      ofType(QuoteTypes.GET_COVERAGE_LIMITS_REQUEST),
      switchMap((action) =>
        from(
          action.isBackOffice === true
            ? SuriApi.getCoverageLimitsBo(action.calculatorName)
            : SuriApi.getCoverageLimits(action.calculatorName),
        ).pipe(
          switchMap((response) =>
            of(QuoteActions.getCoverageLimitsRequestSuccess(action.calculatorName, response.data.data)),
          ),
          catchError((e) => of(GeneralActions.onChangeRequestFailure(e, ERROR_TYPES.WARNING))),
        ),
      ),
    ),
  (action$) =>
    action$.pipe(
      ofType(QuoteTypes.GET_ALL_COVERAGE_LIMITS_REQUEST),
      switchMap((action) =>
        from(action.isBackOffice === true ? SuriApi.getAllCoverageLimitsBo() : SuriApi.getAllCoverageLimits()).pipe(
          switchMap((response) => of(QuoteActions.getAllCoverageLimitsRequestSuccess(response.data.data))),
          catchError((e) => of(GeneralActions.onChangeRequestFailure(e, ERROR_TYPES.WARNING))),
        ),
      ),
    ),
]

export default QuoteEpics
